import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getInvoicesList } from 'src/services/DocumentsService';
import { getFormattedAmount } from 'src/utils/CommonFunctions';

// ─── State Interface ───────────────────────────────────────────────────────────

interface IDocumentHubState {
    // Invoices
    invoices: any[];
    invoicesTotalRecords: number;
    invoicesLoading: boolean;
    invoicesError: string | null;
}

const initialState: IDocumentHubState = {
    invoices: [],
    invoicesTotalRecords: 0,
    invoicesLoading: false,
    invoicesError: null,
};

// ─── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchInvoices = createAsyncThunk('documentHub/fetchInvoices', async (params: any, { rejectWithValue }) => {
    try {
        // Interceptor already unwraps axios response.data, so `response` is the full API body: { message, data, pagination }
        const response: any = await getInvoicesList(params);

        return response;
    } catch (error: any) {
        return rejectWithValue(error?.response?.data?.message ?? 'Failed to fetch invoices');
    }
});

// ─── Slice ────────────────────────────────────────────────────────────────────

export const DocumentHubSlice = createSlice({
    name: 'documentHub',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Invoices
        builder
            .addCase(fetchInvoices.pending, (state) => {
                state.invoicesLoading = true;
                state.invoicesError = null;
                state.invoices = [];
                // Keep previous totalRecords so pagination stays visible during loading
            })
            .addCase(fetchInvoices.fulfilled, (state, action: PayloadAction<any>) => {
                state.invoicesLoading = false;
                // Normalize API field names to match InvoiceRow expectations
                state.invoices = (action.payload?.data ?? []).map((item: any) => ({
                    ...item,
                    status: item.invoiceStatus,
                    // Table "Total Price" cell — "80,850.62 CAD" (thousands separators, 2 decimals)
                    totalPrice: item.totalAmount != null ? `${getFormattedAmount(item.totalAmount)} ${item.currency ?? ''}`.trim() : null,
                    lastUpdated: item.invoiceUpdatedAt ?? item.lastUpdated ?? item.updatedAt,
                }));
                state.invoicesTotalRecords = action.payload?.pagination?.totalRecords ?? 0;
            })
            .addCase(fetchInvoices.rejected, (state, action: PayloadAction<any>) => {
                state.invoicesLoading = false;
                state.invoicesError = action.payload;
            });
    },
});

export default DocumentHubSlice.reducer;
